import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listAllTutorRequests, respondToRequest } from "../../data/requests";
import { getMyAvailability, saveMyAvailability, blockSlot, unblockSlot } from "../../data/me";
import { toId } from "../../lib/subjects";
import { getRequestStatusLabel } from "../../lib/requestStatus";
import coinImg from "../../assets/178F722D-F252-429C-9773-CB27053B84EF.png";

const brand = { bg: "#272c39" };

async function authedFetch(path, { method = "GET", body } = {}) {
  const base =
    process.env.REACT_APP_API_URL ||
    "http://localhost:3001";

  let auth = null;
  try {
    auth = JSON.parse(localStorage.getItem("auth") || "null");
  } catch {
    auth = null;
  }

  const token = auth?.token;
  if (!token) {
    const err = new Error("NO_AUTH");
    err.status = 401;
    throw err;
  }

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || "REQUEST_FAILED");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function CoinAmount({ value, size = 20, className = "", imgClassName = "" }) {
  const amount = Number.isFinite(Number(value)) ? Number(value).toFixed(0) : "0";

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={coinImg}
        alt=""
        className={imgClassName || "object-contain"}
        style={{ width: size, height: size }}
      />
      <span>{amount}</span>
    </span>
  );
}

function SectionCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-[28px] bg-white/[0.055] ring-1 ring-white/10 backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );
}

function EmptyCard({ title, text, actionLabel, onAction }) {
  return (
    <div className="mt-4 rounded-[26px] border border-white/10 bg-white/[0.04] px-6 py-10 text-center">
      {title ? <div className="text-2xl font-semibold text-white">{title}</div> : null}
      {text ? (
        <div className="mx-auto mt-2 max-w-xl text-sm leading-6 text-white/65">{text}</div>
      ) : null}

      {actionLabel && onAction ? (
        <button
          onClick={onAction}
          className="mt-5 rounded-2xl bg-[#7269e3] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(114,105,227,0.28)] hover:brightness-110"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, subvalue, accent = false, icon = null }) {
  return (
    <div
      className={`rounded-[26px] p-5 ring-1 ${
        accent
          ? "bg-[#7269e3]/16 ring-[#7269e3]/30 shadow-[0_14px_40px_rgba(114,105,227,0.18)]"
          : "bg-white/[0.05] ring-white/10"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-white/60">{label}</div>
        {icon ? <div className="shrink-0 text-white/45">{icon}</div> : null}
      </div>

      <div className="mt-3 text-4xl font-extrabold tracking-tight text-white">{value}</div>

      {subvalue ? <div className="mt-2 text-sm text-white/55">{subvalue}</div> : null}
    </div>
  );
}

function Badge({ children, tone = "default" }) {
  const styles = {
    default: "bg-white/10 text-white/85 ring-white/10",
    green: "bg-[#98dea3]/16 text-[#b8f0c1] ring-[#98dea3]/20",
    purple: "bg-[#7269e3]/16 text-[#d6d2ff] ring-[#7269e3]/25",
    amber: "bg-amber-300/14 text-amber-100 ring-amber-200/15",
    red: "bg-red-400/14 text-red-100 ring-red-300/15",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1 ${styles[tone]}`}
    >
      {children}
    </span>
  );
}

function IconSpark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
      <path
        d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconChat() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
      <path
        d="M7 17l-3 3V6a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H7z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
      <path
        d="M5 12l4.2 4.2L19 6.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Availability Editor ─────────────────────────────────────────── */
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const HOUR_OPTIONS = Array.from({ length: 16 }, (_, i) => {
  const h = i + 7; // 07:00 – 22:00
  return `${String(h).padStart(2, "0")}:00`;
});

function expandConcrete(availability, blockedSlots) {
  if (!Array.isArray(availability) || availability.length === 0) return [];
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const blocked = new Set((blockedSlots || []).map((s) => `${s.date}|${s.time}`));
  const results = [];
  for (let d = 0; d < 14; d++) {
    const date = new Date(now);
    date.setDate(now.getDate() + d);
    const dayOfWeek = date.getDay();
    const dateStr = date.toISOString().slice(0, 10);
    for (const slot of availability) {
      if (slot.day !== dayOfWeek) continue;
      const startH = parseInt(slot.start, 10);
      const endH = parseInt(slot.end, 10);
      for (let h = startH; h < endH; h++) {
        const time = `${String(h).padStart(2, "0")}:00`;
        if (dateStr === todayStr) {
          const slotDate = new Date(`${dateStr}T${time}:00`);
          if (slotDate <= now) continue;
        }
        results.push({ dateStr, time, isBlocked: blocked.has(`${dateStr}|${time}`) });
      }
    }
  }
  return results;
}

function AvailabilityEditor() {
  const { t } = useTranslation();
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("schedule"); // "schedule" | "calendar"
  const [toggling, setToggling] = useState(null); // "date|time" key being toggled

  useEffect(() => {
    if (!open || loaded) return;
    getMyAvailability()
      .then(({ availability, blockedSlots: bs }) => {
        setScheduleSlots(availability);
        setBlockedSlots(bs);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded]);

  /* ── Weekly schedule helpers ── */
  const addSlot = (day) => { setScheduleSlots((p) => [...p, { day, start: "09:00", end: "12:00" }]); setSaved(false); };
  const removeSlot = (idx) => { setScheduleSlots((p) => p.filter((_, i) => i !== idx)); setSaved(false); };
  const updateSlot = (idx, field, val) => {
    setScheduleSlots((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const updated = { ...s, [field]: val };
        if (field === "start" && updated.end <= val) {
          updated.end = `${String(Math.min(parseInt(val, 10) + 1, 22)).padStart(2, "0")}:00`;
        }
        return updated;
      })
    );
    setSaved(false);
  };
  const handleSave = async () => {
    setSaving(true);
    try { await saveMyAvailability(scheduleSlots); setSaved(true); }
    catch (e) { alert("Помилка: " + (e?.message || "невідома")); }
    finally { setSaving(false); }
  };
  const slotsForDay = (day) => scheduleSlots.map((s, i) => ({ ...s, _idx: i })).filter((s) => s.day === day);

  /* ── Concrete calendar toggle ── */
  const handleToggle = async (dateStr, time, isBlocked) => {
    const key = `${dateStr}|${time}`;
    setToggling(key);
    try {
      if (isBlocked) {
        const res = await unblockSlot(dateStr, time);
        setBlockedSlots(Array.isArray(res?.blockedSlots) ? res.blockedSlots : blockedSlots.filter((s) => !(s.date === dateStr && s.time === time)));
      } else {
        const res = await blockSlot(dateStr, time);
        setBlockedSlots(Array.isArray(res?.blockedSlots) ? res.blockedSlots : [...blockedSlots, { date: dateStr, time }]);
      }
    } catch (e) { alert("Помилка: " + (e?.message || "")); }
    finally { setToggling(null); }
  };

  /* ── Concrete calendar grouping ── */
  const concrete = expandConcrete(scheduleSlots, blockedSlots);
  const byDate = {};
  for (const s of concrete) {
    if (!byDate[s.dateStr]) byDate[s.dateStr] = [];
    byDate[s.dateStr].push(s);
  }
  const concreteDates = Object.keys(byDate).sort();

  const totalSlots = scheduleSlots.length;

  return (
    <div className="rounded-[28px] bg-white/[0.055] ring-1 ring-white/10">
      {/* Header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 p-5 text-left"
      >
        <div>
          <h2 className="font-unbounded text-lg font-bold text-white">{t("dashboardtutor.schedule.title")}</h2>
          <p className="mt-0.5 text-xs text-white/45">
            {totalSlots > 0 ? t("dashboardtutor.schedule.slots", { count: totalSlots, blocked: blockedSlots.length }) : t("dashboardtutor.schedule.noSlots")}
          </p>
        </div>
        <svg viewBox="0 0 24 24" className={`h-5 w-5 shrink-0 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-white/[0.08]">
          {/* Tabs */}
          <div className="flex gap-1 p-3">
            {[
              { key: "schedule", label: t("dashboardtutor.tabs.schedule") },
              { key: "calendar", label: t("dashboardtutor.tabs.manage") },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 rounded-xl py-2 text-xs font-semibold transition ${
                  activeTab === key ? "bg-[#7269e3] text-white" : "text-white/50 hover:text-white/75"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {!loaded ? (
            <p className="px-5 pb-5 text-sm text-white/40">{t("dashboardtutor.schedule.loading")}</p>
          ) : activeTab === "schedule" ? (
            /* ── Tab 1: Weekly schedule ── */
            <div className="px-5 pb-5">
              <p className="mb-4 text-xs text-white/45 leading-relaxed">
                {t("dashboardtutor.schedule.hint")}
              </p>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 0].map((day) => {
                  const daySlots = slotsForDay(day);
                  return (
                    <div key={day}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-white/70 uppercase tracking-wide">
                          {t(`dashboardtutor.days.${DAY_KEYS[day]}`)}
                        </span>
                        <button onClick={() => addSlot(day)} className="text-xs text-[#7269e3] hover:text-[#a09de8] flex items-center gap-1">
                          <span className="text-base leading-none">+</span> {t("dashboardtutor.schedule.addSlot")}
                        </button>
                      </div>
                      {daySlots.length === 0 ? (
                        <p className="text-xs text-white/25 pl-1">{t("dashboardtutor.schedule.dayOff")}</p>
                      ) : (
                        <div className="space-y-1.5">
                          {daySlots.map(({ _idx, start, end }) => (
                            <div key={_idx} className="flex items-center gap-2">
                              <select value={start} onChange={(e) => updateSlot(_idx, "start", e.target.value)}
                                className="rounded-xl bg-white/[0.07] px-2 py-1.5 text-xs text-white ring-1 ring-white/10 focus:outline-none focus:ring-[#7269e3] cursor-pointer">
                                {HOUR_OPTIONS.slice(0, -1).map((h) => <option key={h} value={h} className="bg-[#2e3447]">{h}</option>)}
                              </select>
                              <span className="text-white/40 text-xs">—</span>
                              <select value={end} onChange={(e) => updateSlot(_idx, "end", e.target.value)}
                                className="rounded-xl bg-white/[0.07] px-2 py-1.5 text-xs text-white ring-1 ring-white/10 focus:outline-none focus:ring-[#7269e3] cursor-pointer">
                                {HOUR_OPTIONS.filter((h) => h > start).map((h) => <option key={h} value={h} className="bg-[#2e3447]">{h}</option>)}
                              </select>
                              <button onClick={() => removeSlot(_idx)} className="ml-auto text-white/30 hover:text-red-400 transition-colors text-lg leading-none">×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`mt-5 w-full rounded-2xl py-2.5 text-sm font-semibold transition ${
                  saved ? "bg-[#98dea3]/20 text-[#98dea3] ring-1 ring-[#98dea3]/30" : "bg-[#7269e3] text-white hover:brightness-110 disabled:opacity-60"
                }`}
              >
                {saving ? t("dashboardtutor.schedule.saving") : saved ? t("dashboardtutor.schedule.saved") : t("dashboardtutor.schedule.save")}
              </button>
            </div>
          ) : (
            /* ── Tab 2: Concrete calendar — block/unblock individual slots ── */
            <div className="px-5 pb-5">
              <p className="mb-3 text-xs text-white/45 leading-relaxed">
                {t("dashboardtutor.schedule.calendarHint")}
              </p>
              {concreteDates.length === 0 ? (
                <p className="text-xs text-white/30 text-center py-4">
                  {t("dashboardtutor.tabs.scheduleHint")}
                </p>
              ) : (
                // Vertical layout: one day per row with wrapping time chips.
                // The old horizontal column grid (min-w-max) overflowed the
                // phone screen, so the schedule was cut off on the right.
                <div className="space-y-3">
                  {concreteDates.map((dateStr) => {
                    const d = new Date(dateStr + "T12:00:00");
                    return (
                      <div key={dateStr} className="flex flex-col gap-2 sm:flex-row sm:items-start">
                        <div className="flex shrink-0 items-baseline gap-1.5 sm:w-20 sm:flex-col sm:items-start sm:gap-0">
                          <span className="text-[11px] font-bold uppercase text-white/50">{t(`dashboardtutor.daysShort.${DAY_KEYS[d.getDay()]}`)}</span>
                          <span className="text-sm font-bold text-white">{d.getDate()}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {byDate[dateStr].map(({ time, isBlocked }) => {
                            const key = `${dateStr}|${time}`;
                            const busy = toggling === key;
                            return (
                              <button
                                key={time}
                                onClick={() => handleToggle(dateStr, time, isBlocked)}
                                disabled={busy}
                                title={isBlocked ? t("dashboardtutor.schedule.unblockHint") : t("dashboardtutor.schedule.blockHint")}
                                className={`w-14 rounded-xl px-1 py-1.5 text-xs font-medium transition-all ${
                                  busy
                                    ? "opacity-50 bg-white/5 text-white/30"
                                    : isBlocked
                                    ? "bg-red-500/15 ring-1 ring-red-500/30 text-red-400 line-through"
                                    : "bg-[#98dea3]/15 ring-1 ring-[#98dea3]/30 text-[#98dea3] hover:bg-red-500/15 hover:ring-red-500/30 hover:text-red-400"
                                }`}
                              >
                                {time}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TutorDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [wallet, setWallet] = useState({ balance: 0, currency: "POINTS" });
  const [walletLoading, setWalletLoading] = useState(false);

  const [toast, setToast] = useState({ type: "", text: "" });
  const [statusFilter, setStatusFilter] = useState("NEW");
  const [search, setSearch] = useState("");
  const [respondingId, setRespondingId] = useState("");

  useEffect(() => {
    if (!toast?.text) return;
    const id = setTimeout(() => setToast({ type: "", text: "" }), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const fmt = useCallback(
    (iso) =>
      new Date(iso).toLocaleString(i18n.language || undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [i18n.language]
  );

  const timeAgo = useCallback(
    (iso) => {
      const ts = new Date(iso).getTime();
      if (!Number.isFinite(ts)) return "";

      const diff = Date.now() - ts;
      const min = Math.floor(diff / 60000);

      if (min < 1) return t("common.justNow");
      if (min < 60) return t("common.minutesAgo", { count: min });

      const hours = Math.floor(min / 60);
      if (hours < 24) return t("common.hoursAgo", { count: hours });

      const days = Math.floor(hours / 24);
      return t("common.daysAgo", { count: days });
    },
    [t]
  );

  // General requests carry a FIELD/sphere key (e.g. digital_product_design),
  // which lives under subjectGroups.*, not subjects.*. Try subject first, then
  // the sphere group, then fall back to the raw value — otherwise the UI shows
  // the untranslated key like "subjects.digital_product_design".
  const locSubject = useCallback((s) => {
    const id = toId(s);
    const subjKey = `subjects.${id}`;
    const subjVal = t(subjKey);
    if (subjVal !== subjKey) return subjVal;
    const groupKey = `subjectGroups.${id}`;
    const groupVal = t(groupKey);
    if (groupVal !== groupKey) return groupVal;
    return s;
  }, [t]);

  const modeLabel = useCallback(
    (mode) => {
      const value = String(mode || "").toLowerCase();
      if (value === "online") return t("common.modeOnline");
      if (value === "in-person" || value === "offline") return t("common.modeOffline");
      return t("common.modeFlexible");
    },
    [t]
  );

  const loadWallet = useCallback(async () => {
    if (!auth?.token) return;

    setWalletLoading(true);
    try {
      const data = await authedFetch("/api/me/wallet");
      setWallet({
        balance: Number.isFinite(Number(data?.balance)) ? Number(data.balance) : 0,
        currency: data?.currency || "POINTS",
      });
    } catch (e) {
      console.warn("Wallet load failed:", e?.message || e);
    } finally {
      setWalletLoading(false);
    }
  }, [auth?.token]);

  const refresh = useCallback(async () => {
    if (!auth?.token) {
      setRows([]);
      setErr("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErr("");

    try {
      const items = await listAllTutorRequests();
      const arr = Array.isArray(items) ? items : [];
      const unique = Array.from(new Map(arr.filter(Boolean).map((r) => [r.id, r])).values());
      unique.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      setRows(unique);
    } catch (e) {
      console.error(e);
      setRows([]);
      setErr(e?.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [auth?.token, t]);

  useEffect(() => {
    refresh();
    loadWallet();
  }, [refresh, loadWallet]);

  const getKind = useCallback((r) => String(r?.kind || "TRIAL").toUpperCase(), []);
  const getStatus = useCallback((r) => String(r?.status || "").toUpperCase(), []);

  const isClosed = useCallback((r) => getStatus(r) === "CLOSED", [getStatus]);
  const isNew = useCallback((r) => getStatus(r) === "NEW", [getStatus]);
  const isChatActive = useCallback((r) => getStatus(r) === "CHAT_ACTIVE", [getStatus]);
  const isTrialDone = useCallback((r) => getStatus(r) === "TRIAL_DONE", [getStatus]);
  const becameClient = useCallback((r) => getStatus(r) === "BECAME_CLIENT", [getStatus]);

  const isUnlocked = useCallback(
    (r) => !!r?.contactsUnlocked || isChatActive(r) || getStatus(r) === "UNLOCKED",
    [isChatActive, getStatus]
  );

  const pricePts = useCallback((r) => {
    const n = Number(r?.priceCredits);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const allRows = useMemo(() => (Array.isArray(rows) ? rows : []).filter(Boolean), [rows]);

  const trialNewCount = useMemo(() => allRows.filter((r) => isNew(r) && getKind(r) === "TRIAL").length, [allRows, isNew, getKind]);
  const taskNewCount = useMemo(() => allRows.filter((r) => isNew(r) && getKind(r) === "TASK").length, [allRows, isNew, getKind]);

  const searchedRows = useMemo(() => {
    if (!search.trim()) return allRows;
    const q = search.trim().toLowerCase();
    return allRows.filter(
      (r) =>
        (r.studentName || "").toLowerCase().includes(q) ||
        (r.subject || "").toLowerCase().includes(q)
    );
  }, [allRows, search]);

  const applyStatusFilter = useCallback(
    (rows) => {
      if (statusFilter === "NEW") return rows.filter((r) => isNew(r));
      if (statusFilter === "ACTIVE") return rows.filter((r) => !isClosed(r) && !isNew(r));
      return rows;
    },
    [statusFilter, isNew, isClosed]
  );

  const trialRows = useMemo(
    () => applyStatusFilter(searchedRows.filter((r) => getKind(r) === "TRIAL")),
    [searchedRows, getKind, applyStatusFilter]
  );

  const taskRows = useMemo(
    () => applyStatusFilter(searchedRows.filter((r) => getKind(r) === "TASK")),
    [searchedRows, getKind, applyStatusFilter]
  );

  const valueLabel = useCallback(
    (r) => {
      if (getKind(r) !== "TASK") return null;

      const budget = Number(r?.budget);
      const fee = pricePts(r);
      if (!Number.isFinite(budget) || budget <= 0 || fee <= 0) return null;

      const ratio = budget / fee;

      if (ratio >= 8) return { tone: "green", text: t("dashboardtutor.value.high") };
      if (ratio >= 4) return { tone: "purple", text: t("dashboardtutor.value.good") };
      if (ratio < 2) return { tone: "amber", text: t("dashboardtutor.value.low") };
      return { tone: "default", text: t("dashboardtutor.value.normal") };
    },
    [getKind, pricePts, t]
  );

  const urgencyLabel = useCallback(
    (r) => {
      if (!r?.preferredAt) return null;

      const target = new Date(r.preferredAt).getTime();
      if (!Number.isFinite(target)) return null;

      const diff = target - Date.now();
      const hours = diff / 3600000;

      if (hours < 0) return { tone: "amber", text: t("dashboardtutor.urgency.past") };
      if (hours <= 6) return { tone: "red", text: t("dashboardtutor.urgency.soon") };
      if (hours <= 24) return { tone: "amber", text: t("dashboardtutor.urgency.today") };

      return null;
    },
    [t]
  );

  const totalNewCount = trialNewCount + taskNewCount;
  const totalUnlocked = useMemo(() => allRows.filter((r) => isUnlocked(r)).length, [allRows, isUnlocked]);
  const totalClients = useMemo(() => allRows.filter((r) => becameClient(r)).length, [allRows, becameClient]);
  const conversionRate = allRows.length > 0 ? Math.round((totalClients / allRows.length) * 100) : 0;

  const minVisibleFee = useMemo(() => {
    const values = allRows.filter((r) => isNew(r)).map((r) => pricePts(r)).filter((n) => Number.isFinite(n) && n > 0);
    return values.length ? Math.min(...values) : 0;
  }, [allRows, isNew, pricePts]);

  const lowBalance = minVisibleFee > 0 && Number(wallet.balance || 0) < minVisibleFee;

  const STATUS_TABS = useMemo(() => [
    { key: "NEW",    label: t("dashboardtutor.filter.new"),    count: allRows.filter((r) => isNew(r)).length },
    { key: "ACTIVE", label: t("dashboardtutor.filter.active"), count: allRows.filter((r) => !isClosed(r) && !isNew(r)).length },
    { key: "ALL",    label: t("dashboardtutor.filter.all"),    count: allRows.length },
  ], [allRows, isNew, isClosed, t]);

  const openChatToStudent = useCallback(
    (r) => {
      const studentId = r?.studentId;
      if (!studentId) return;
      const name = r?.studentName || t("common.student");
      navigate(`/chat/${studentId}?name=${encodeURIComponent(name)}`);
    },
    [navigate, t]
  );

  const onRespond = useCallback(
    async (r) => {
      if (!r?.id || respondingId) return;

      try {
        setToast({ type: "", text: "" });
        setRespondingId(r.id);

        await respondToRequest(r.id);
        await refresh();
        await loadWallet();

        setToast({
          type: "success",
          text: t("dashboardtutor.toast.unlockedWithFee", {
            fee: pricePts(r),
            count: pricePts(r),
          }),
        });

        openChatToStudent(r);
      } catch (e) {
        if (e?.status === 402) {
          setToast({
            type: "error",
            text: t("dashboardtutor.toast.needTopup"),
          });
          return;
        }

        setToast({
          type: "error",
          text: e?.message || t("common.error"),
        });
      } finally {
        setRespondingId("");
      }
    },
    [respondingId, refresh, loadWallet, openChatToStudent, pricePts, t]
  );

  const renderStatusBadge = useCallback(
    (r) => {
      if (isNew(r)) return <Badge tone="green">{getRequestStatusLabel(t, "NEW")}</Badge>;
      if (becameClient(r)) return <Badge tone="purple">{getRequestStatusLabel(t, "BECAME_CLIENT")}</Badge>;
      if (isTrialDone(r)) return <Badge tone="amber">{getRequestStatusLabel(t, "TRIAL_DONE")}</Badge>;
      if (isUnlocked(r)) return <Badge tone="default">{getRequestStatusLabel(t, "CHAT_ACTIVE")}</Badge>;
      return null;
    },
    [becameClient, isNew, isTrialDone, isUnlocked, t]
  );

  return (
    <main className="min-h-screen" style={{ background: brand.bg, color: "white" }}>
      <div className="mx-auto max-w-6xl px-4 pb-20 pt-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-unbounded text-3xl font-extrabold tracking-tight md:text-4xl">
              {t("dashboardtutor.tutor.welcomeBack")}
              {auth?.name ? `, ${auth.name}` : ""}
            </h1>

            <p className="mt-2 max-w-2xl text-base text-white/72">
              {t("dashboardtutor.tutor.subtitle")}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                refresh();
                loadWallet();
              }}
              className="rounded-2xl border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-medium text-white/90 hover:bg-white/[0.06]"
            >
              {t("actions.refresh")}
            </button>

            <Link
              to="/onboarding/tutor"
              className="rounded-2xl bg-[#7269e3] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(114,105,227,0.28)] hover:brightness-110"
            >
              {t("dashboardtutor.tutor.editProfile")}
            </Link>
          </div>
        </header>

        {toast?.text ? (
          <div
            className={`mt-6 rounded-[22px] px-4 py-3 text-sm ring-1 ${
              toast.type === "error"
                ? "bg-red-400/10 text-red-100 ring-red-400/25"
                : "bg-white/10 text-white ring-white/15"
            }`}
          >
            {toast.text}
          </div>
        ) : null}

        {lowBalance ? (
          <div className="mt-6 rounded-[26px] bg-[#7269e3]/14 p-4 ring-1 ring-[#7269e3]/30">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="font-semibold text-white">
                  {t("dashboardtutor.wallet.lowBalanceTitle")}
                </div>
                <div className="mt-1 text-sm text-white/72">
                  {t("dashboardtutor.wallet.lowBalanceText")}
                </div>
              </div>

              <button
                onClick={() => navigate("/pay?mode=topup")}
                className="rounded-2xl bg-[#7269e3] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(114,105,227,0.28)] hover:brightness-110"
              >
                {t("dashboardtutor.wallet.topup")}
              </button>
            </div>
          </div>
        ) : null}

        <section className="mt-6 grid gap-3 grid-cols-2 md:grid-cols-4">
          <StatCard
            accent
            label={t("dashboardtutor.stats.newRequests")}
            value={totalNewCount}
            subvalue={t("dashboardtutor.stats.awaiting")}
            icon={<IconSpark />}
          />
          <StatCard
            label={t("dashboardtutor.stats.activeChats")}
            value={totalUnlocked}
            subvalue={t("dashboardtutor.stats.inProgress")}
            icon={<IconChat />}
          />
          <StatCard
            label={t("dashboardtutor.stats.clients")}
            value={totalClients}
            subvalue={t("dashboardtutor.stats.clientsSub")}
            icon={<IconCheck />}
          />
          <StatCard
            label={t("dashboardtutor.stats.conversion")}
            value={`${conversionRate}%`}
            subvalue={t("dashboardtutor.stats.conversionSub")}
          />
        </section>

        {/* ── Search + status filter ── */}
        <div className="mt-6 space-y-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dashboardtutor.searchPlaceholder")}
            className="w-full rounded-2xl bg-white/[0.05] px-4 py-3 text-sm text-white placeholder-white/35 ring-1 ring-white/10 focus:outline-none focus:ring-[#7269e3]"
          />
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                  statusFilter === key
                    ? "bg-[#7269e3] text-white shadow-[0_8px_24px_rgba(114,105,227,0.28)]"
                    : "bg-white/[0.05] text-white/70 ring-1 ring-white/10 hover:bg-white/[0.08]"
                }`}
              >
                {label}
                {count > 0 && (
                  <span className="ml-2 rounded-full bg-black/20 px-1.5 py-0.5 text-xs">
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <section className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(320px,1fr)]">
          {/* ── Two request sections ── */}
          {/* min-w-0: keep grid children shrinkable on narrow phones */}
          <div className="min-w-0 space-y-6">
            {[
              {
                label:    t("dashboardtutor.crm.trialSection"),
                subtitle: t("dashboardtutor.crm.trialSectionHint"),
                rows:     trialRows,
                accent:   "#98dea3",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                  </svg>
                ),
              },
              {
                label:    t("dashboardtutor.crm.taskSection"),
                subtitle: t("dashboardtutor.crm.taskSectionHint"),
                rows:     taskRows,
                accent:   "#7269e3",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M16.881 4.345A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 0 1-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0 0 18 11.25c0-2.414-.393-4.735-1.119-6.905ZM18.26 3.74a23.22 23.22 0 0 1 1.24 7.51 23.22 23.22 0 0 1-1.24 7.51c-.055.161-.111.321-.17.48A.75.75 0 0 0 18.75 20a.75.75 0 0 0 .68-.43 24.716 24.716 0 0 0 1.57-9.32 24.716 24.716 0 0 0-1.57-9.32.75.75 0 0 0-.68-.43.75.75 0 0 0-.29 1.24Z"/>
                  </svg>
                ),
              },
            ].map(({ label: sectionLabel, subtitle: sectionSubtitle, rows: sectionRows, accent, icon }) => (
              <SectionCard key={sectionLabel} className="overflow-hidden">
                {/* Accent top bar */}
                <div className="h-1 w-full" style={{ background: accent, opacity: 0.7 }} />
                <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: `${accent}20`, color: accent }}>
                      {icon}
                    </div>
                    <div>
                      <h2 className="font-unbounded text-lg font-bold text-white leading-tight">{sectionLabel}</h2>
                      <p className="mt-0.5 text-sm text-white/45">{sectionSubtitle}</p>
                    </div>
                  </div>
                  <span className="shrink-0 text-sm text-white/40">{sectionRows.length} {t("dashboardtutor.crm.records")}</span>
                </div>

                {loading ? (
                  <EmptyCard title={t("common.loading")} text={t("dashboardtutor.loadingText")} />
                ) : err ? (
                  <EmptyCard title={t("common.error")} text={err} actionLabel={t("actions.refresh")} onAction={() => { refresh(); loadWallet(); }} />
                ) : sectionRows.length === 0 ? (
                  <EmptyCard
                    title={search ? t("dashboardtutor.crm.notFound") : t("dashboardtutor.crm.emptyTitle")}
                    text={search ? t("dashboardtutor.crm.notFoundText") : t("dashboardtutor.crm.emptyText")}
                  />
                ) : (
                  <ul className="mt-5 space-y-4">
                    {sectionRows.map((r) => {
                      const requestValue = valueLabel(r);
                      const urgency = urgencyLabel(r);
                      const fee = pricePts(r);
                      const locked = !isUnlocked(r);
                      const cannotAfford = locked && Number(wallet.balance || 0) < fee;
                      const isBusy = respondingId === r.id;
                      const statusClosed = isClosed(r);

                      return (
                        <li
                          key={r.id}
                          className={`rounded-[24px] p-5 ring-1 transition ${
                            statusClosed
                              ? "bg-white/[0.02] ring-white/6 opacity-60"
                              : "bg-white/[0.06] ring-white/10 hover:bg-white/[0.075]"
                          }`}
                        >
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-2">
                                {renderStatusBadge(r)}
                                {urgency ? <Badge tone={urgency.tone}>{urgency.text}</Badge> : null}
                                {requestValue ? <Badge tone={requestValue.tone}>{requestValue.text}</Badge> : null}
                              </div>
                              <span className="text-xs text-white/40">{timeAgo(r.updatedAt || r.createdAt)}</span>
                            </div>

                            <div>
                              <div className="text-lg font-semibold text-white">
                                {r.studentName || t("common.student")} <span className="text-white/40">·</span> {locSubject(r.subject)}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/55">
                                <span>{modeLabel(r.mode)}</span>
                                {r.level ? <span>{t(`levels.${toId(r.level)}`)}</span> : null}
                                {r.preferredAt ? <span>{fmt(r.preferredAt)}</span> : null}
                              </div>
                            </div>

                            {!isUnlocked(r) && (
                              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/[0.08] px-3 py-1.5 text-sm text-white/88 ring-1 ring-white/10">
                                {t("dashboardtutor.fee")}
                                <CoinAmount value={fee} size={16} className="font-semibold text-[#98DEA3]" />
                              </span>
                            )}

                            {r.message ? (
                              <div className="rounded-2xl bg-black/10 px-4 py-3 text-sm leading-6 text-white/70 line-clamp-2">
                                {r.message}
                              </div>
                            ) : null}

                            {!statusClosed && (
                              <div className="flex flex-wrap items-center gap-2">
                                {locked ? (
                                  <button
                                    onClick={() => onRespond(r)}
                                    disabled={isBusy || cannotAfford}
                                    className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                                      cannotAfford
                                        ? "bg-white/[0.08] text-white/50 ring-1 ring-white/10"
                                        : "bg-[#7269e3] text-white shadow-[0_10px_30px_rgba(114,105,227,0.24)] hover:brightness-110"
                                    } ${isBusy ? "opacity-70" : ""}`}
                                  >
                                    {isBusy ? t("actions.opening") : cannotAfford ? t("dashboardtutor.wallet.notEnough") : t("actions.openChat")}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => openChatToStudent(r)}
                                    className="rounded-2xl bg-[#7269e3] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(114,105,227,0.24)] hover:brightness-110"
                                  >
                                    {t("actions.openChat")}
                                  </button>
                                )}
                                <button
                                  onClick={() => navigate(`/requests/${r.id}`)}
                                  className="rounded-2xl border border-white/12 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-white/85 hover:bg-white/[0.06]"
                                >
                                  {t("actions.details")}
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                </div>
              </SectionCard>
            ))}
          </div>

          {/* ── Right sidebar: wallet only ── */}
          <div className="space-y-6">
            <SectionCard className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-unbounded text-[28px] font-bold leading-none text-white">
                    {t("dashboardtutor.wallet.title")}
                  </h2>
                  <div className="mt-2 text-sm text-white/52">
                    {Number(wallet.balance || 0) > 0
                      ? t("dashboardtutor.wallet.ready")
                      : t("dashboardtutor.wallet.lowBalanceText")}
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-[24px] bg-white/[0.05] p-5 ring-1 ring-white/10">
                <div className="text-sm text-white/60">{t("dashboardtutor.wallet.available")}</div>
                <div className="mt-3">
                  {walletLoading ? (
                    <div className="text-4xl font-extrabold tracking-tight text-white">…</div>
                  ) : (
                    <CoinAmount value={wallet.balance} size={34} className="text-4xl font-extrabold text-white" />
                  )}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    onClick={() => navigate("/pay?mode=topup")}
                    className="rounded-2xl bg-[#7269e3] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(114,105,227,0.24)] hover:brightness-110"
                  >
                    {t("dashboardtutor.wallet.topup")}
                  </button>
                  <button
                    onClick={loadWallet}
                    className="rounded-2xl border border-white/12 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-white/85 hover:bg-white/[0.06]"
                  >
                    {t("dashboardtutor.wallet.refresh")}
                  </button>
                </div>
                <button
                  onClick={() => navigate("/transactions")}
                  className="mt-3 flex items-center gap-2 rounded-xl px-1 py-1 text-xs text-white/52 hover:text-white/75"
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-white/10">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none">
                      <path d="M7 7h10M7 12h10M7 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  {t("dashboardtutor.wallet.history")}
                </button>
                <p className="mt-4 text-sm leading-6 text-white/55">
                  {t("dashboardtutor.wallet.hint")}
                </p>
              </div>
            </SectionCard>

            {/* ── Availability editor ── */}
            <AvailabilityEditor />
          </div>
        </section>
      </div>
    </main>
  );
}